/**
 * Withholding Tax Register and remittances (FSD 2.2.2.10 "withholding computed, deducted
 * and remitted by period and authority", FIN-FR-006, data model `ap.withholding` with
 * remittance_status; FT10-U16 over the FT7-B30 routes).
 *
 * Thin wrappers over the generated client. Two rules the server owns and this layer
 * only mirrors in what it offers:
 *
 *  - **Dual authorisation.** Drafting a remittance (`ap.wht.prepare`) posts nothing; posting
 *    it (`ap.wht.remit`, step-up MFA) credits the bank, and the service refuses a poster who
 *    prepared the draft, and a reverser who posted it, with ERR_SOD_VIOLATION.
 *  - **One authority, one period per remittance.** Every selected record must be owed to
 *    the named authority and withheld on or before the period's end and the remittance date.
 *
 * The register export goes through `rawRequest` because its body IS the CSV and the
 * generated `apReportsWithholdingExport` (operation `ap.reports.withholdingExport`)
 * returns `void`; the server audits every export.
 */

import {
  apReportsWithholding,
  apWhtRemittancesCancel,
  apWhtRemittancesCreate,
  apWhtRemittancesList,
  apWhtRemittancesPost,
  apWhtRemittancesReverse,
  cashBankAccountsList,
  type ApCreateWhtRemittanceRequest,
  type ApWhtRemittanceDTO,
  type ApWhtRemittanceListQuery,
  type ApWithholdingRegisterDTO,
  type ApWithholdingRegisterQuery,
  type ApWithholdingRegisterRowDTO,
  type PageApWhtRemittanceDTO,
} from '@erpx/api'

import { api, rawRequest } from '@/api/client'
import { MAX_LIMIT } from '@/composables/useTableUrlState'

export type {
  ApCreateWhtRemittanceRequest,
  ApWhtRemittanceDTO,
  ApWithholdingRegisterDTO,
  ApWithholdingRegisterRowDTO,
  PageApWhtRemittanceDTO,
}

/** One cash bank account, as `cash.bankAccounts.list` returns it. */
export type PayingAccount = Awaited<ReturnType<typeof cashBankAccountsList>>['items'][number]

export const WITHHOLDING_PERMISSIONS = {
  /** Read the register and remittances, and export the register (audited). */
  whtRead: 'ap.wht.read',
  /** Draft and cancel a remittance. Posts nothing. */
  whtPrepare: 'ap.wht.prepare',
  /** Post (not one's own draft) and reverse (not one's own posting). Step-up MFA. */
  whtRemit: 'ap.wht.remit',
  companyRead: 'org.company.read',
  /** Listing the bank accounts a remittance can be paid from. */
  bankAccountRead: 'cash.account.read',
} as const

export const REMIT_STATES = ['unremitted', 'draft', 'remitted'] as const
export type RemitState = (typeof REMIT_STATES)[number]

export const REMITTANCE_STATUSES = ['draft', 'posted', 'cancelled', 'reversed'] as const
export type RemittanceStatus = (typeof REMITTANCE_STATUSES)[number]

export const SOD_VIOLATION_CODE = 'ERR_SOD_VIOLATION'
export const WHT_ALREADY_REMITTED_CODE = 'ERR_WHT_ALREADY_REMITTED'

export interface RegisterFilters {
  companyId: number
  from: string
  to: string
  authority: string
  status: RemitState | ''
}

export interface RemittanceFilters {
  companyId: number
  status: RemittanceStatus | ''
  cursor: string
}

export const WITHHOLDING_KEYS = {
  all: ['ap', 'withholding'] as const,
  register: (filters: RegisterFilters) => ['ap', 'withholding', 'register', filters] as const,
  remittances: (filters: RemittanceFilters) => ['ap', 'withholding', 'remittances', filters] as const,
  payingAccounts: (companyId: number) => ['ap', 'withholding', 'paying-accounts', companyId] as const,
} as const

export function registerQuery(filters: RegisterFilters): ApWithholdingRegisterQuery {
  return {
    companyId: filters.companyId,
    ...(filters.from === '' ? {} : { from: filters.from }),
    ...(filters.to === '' ? {} : { to: filters.to }),
    ...(filters.authority === '' ? {} : { authority: filters.authority }),
    ...(filters.status === '' ? {} : { status: filters.status }),
  }
}

export function withholdingRegister(filters: RegisterFilters): Promise<ApWithholdingRegisterDTO> {
  return apReportsWithholding(api(), registerQuery(filters))
}

function filenameFrom(disposition: string | null, fallback: string): string {
  if (disposition === null) return fallback
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const name = match?.[1]?.trim()
  if (name === undefined || name === '') return fallback
  try {
    return decodeURIComponent(name)
  } catch {
    return name
  }
}

/**
 * Download the register as the server renders it (`GET /reports/withholding/export`,
 * operation `ap.reports.withholdingExport`). The export is audited server-side, which a
 * client-built CSV would not be.
 */
export async function exportWithholdingRegister(
  filters: RegisterFilters,
  fallbackFilename: string,
): Promise<{ filename: string; bytes: number }> {
  const query: Record<string, string> = {}
  for (const [key, value] of Object.entries(registerQuery(filters))) {
    if (value !== undefined) query[key] = String(value)
  }
  const response = await rawRequest('GET', '/api/v1/reports/withholding/export', query)
  const blob = await response.blob()
  const filename = filenameFrom(response.headers.get('Content-Disposition'), fallbackFilename)

  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
  return { filename, bytes: blob.size }
}

export function listRemittances(filters: RemittanceFilters): Promise<PageApWhtRemittanceDTO> {
  const query: ApWhtRemittanceListQuery = {
    companyId: filters.companyId,
    limit: 50,
    ...(filters.status === '' ? {} : { status: filters.status }),
    ...(filters.cursor === '' ? {} : { cursor: filters.cursor }),
  }
  return apWhtRemittancesList(api(), query)
}

export function createRemittance(
  body: ApCreateWhtRemittanceRequest,
  idempotencyKey: string,
): Promise<ApWhtRemittanceDTO> {
  return apWhtRemittancesCreate(api(), body, idempotencyKey)
}

/** Dr withholding tax payable / Cr the bank. Step-up MFA; never the preparer. */
export function postRemittance(
  id: number,
  reference: string,
  idempotencyKey: string,
): Promise<ApWhtRemittanceDTO> {
  return apWhtRemittancesPost(
    api(),
    { id: String(id) },
    reference === '' ? {} : { reference },
    idempotencyKey,
  )
}

export function cancelRemittance(
  id: number,
  reason: string,
  idempotencyKey: string,
): Promise<ApWhtRemittanceDTO> {
  return apWhtRemittancesCancel(api(), { id: String(id) }, { reason }, idempotencyKey)
}

/** Step-up MFA; never the person who posted it. */
export function reverseRemittance(
  id: number,
  reason: string,
  reversalDate: string,
  idempotencyKey: string,
): Promise<ApWhtRemittanceDTO> {
  return apWhtRemittancesReverse(
    api(),
    { id: String(id) },
    { reason, ...(reversalDate === '' ? {} : { reversalDate }) },
    idempotencyKey,
  )
}

const MAX_PAGES = 10

/** The legal entity's active bank accounts, walked to the end (a remittance pays from one). */
export async function listPayingAccounts(companyId: number): Promise<PayingAccount[]> {
  const items: PayingAccount[] = []
  let cursor = ''
  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await cashBankAccountsList(api(), {
      companyId,
      kind: 'bank',
      isActive: true,
      limit: MAX_LIMIT,
      ...(cursor === '' ? {} : { cursor }),
    })
    items.push(...result.items)
    if (!result.hasMore || result.nextCursor === null || result.nextCursor === '') break
    cursor = result.nextCursor
  }
  return items
}

/** `2026-08-14` -> `2026-08`. */
export function monthOf(date: string): string {
  return date.slice(0, 7)
}

/** The latest month any of the records was withheld in: the earliest period that can remit them all. */
export function earliestRemittablePeriod(rows: readonly ApWithholdingRegisterRowDTO[]): string {
  let latest = ''
  for (const row of rows) {
    const month = monthOf(row.withheldOn)
    if (month > latest) latest = month
  }
  return latest
}

/** Today as a business date in the tenant's timezone (`YYYY-MM-DD`). */
export function todayIn(timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone === undefined || timeZone === '' ? undefined : timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date())
  } catch {
    return new Date().toISOString().slice(0, 10)
  }
}
